import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Box,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Search,
  FilterList,
  CheckCircle,
  Cancel,
  Visibility,
  Edit,
  VpnKey,
  Send,
  ContentCopy,
  Save,
  Close,
  EventAvailable,
} from '@mui/icons-material';
import adminService from '../services/adminService';
import {
  getStatusColor,
  formatDate,
  formatCurrency,
  formatDateTime,
} from '../utils/helpers';

const UserManagement = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState(location.state?.statusFilter || '');
  const [userTypeFilter, setUserTypeFilter] = useState(location.state?.userTypeFilter || '');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordInfo, setPasswordInfo] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    // Apply filters from navigation state
    if (location.state?.statusFilter) {
      setStatusFilter(location.state.statusFilter);
    }
    if (location.state?.userTypeFilter) {
      setUserTypeFilter(location.state.userTypeFilter);
    }
  }, [location.state]);

  useEffect(() => {
    loadUsers();
  }, [searchTerm, statusFilter, userTypeFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (userTypeFilter) params.user_type = userTypeFilter;

      const response = await adminService.getUsers(params);
      setUsers(response.results || response);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('שגיאה בטעינת משתמשים');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleUserAction = useCallback(async (userId, action) => {
    try {
      setActionLoading(true);
      let response;
      if (action === 'activate') {
        response = await adminService.updateUserStatus(userId, 'active');
      } else if (action === 'deactivate') {
        response = await adminService.updateUserStatus(userId, 'inactive');
      } else if (action === 'suspend') {
        response = await adminService.updateUserStatus(userId, 'suspended');
      } else if (action === 'pending') {
        response = await adminService.updateUserStatus(userId, 'pending');
      }

      if (response.success) {
        setUsers(prevUsers => prevUsers.map(user =>
          user.id === userId ? { ...user, ...response.user } : user
        ));
        showSnackbar(response.message);
        setError('');
      }
    } catch (error) {
      console.error(`Error ${action} user:`, error);
      setError(`שגיאה בעדכון סטטוס משתמש`);
      showSnackbar('שגיאה בעדכון סטטוס משתמש', 'error');
    } finally {
      setActionLoading(false);
    }
  }, []);

  const handleViewUser = useCallback(async (userId) => {
    try {
      const userDetails = await adminService.getUserDetails(userId);
      setSelectedUser(userDetails);
      setDialogOpen(true);
    } catch (error) {
      console.error('Error loading user details:', error);
      setError('שגיאה בטעינת פרטי משתמש');
      showSnackbar('שגיאה בטעינת פרטי משתמש', 'error');
    }
  }, []);

  const handleEditUser = useCallback(async (user) => {
    try {
      const userDetails = await adminService.getUserDetails(user.id);
      setEditingUser({ ...userDetails });
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error loading user details for edit:', error);
      showSnackbar('שגיאה בטעינת פרטי משתמש', 'error');
    }
  }, []);

  const handleSaveUser = async () => {
    try {
      setActionLoading(true);
      const response = await adminService.updateUser(editingUser.id, editingUser);
      
      setUsers(users.map(user => 
        user.id === editingUser.id ? { ...user, ...response } : user
      ));
      
      showSnackbar('פרטי המשתמש עודכנו בהצלחה');
      setEditDialogOpen(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      showSnackbar('שגיאה בעדכון פרטי המשתמש', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePasswordAction = useCallback(async (userId, action, includeEmail = false, password = '') => {
    try {
      setActionLoading(true);
      let response;

      if (action === 'get') {
        response = await adminService.getUserPassword(userId);
        setPasswordInfo(response);
        setPasswordDialogOpen(true);
      } else if (action === 'send') {
        response = await adminService.sendPasswordToUser(userId);
        showSnackbar('הסיסמה נשלחה בהצלחה למשתמש');
      } else if (action === 'reset') {
        response = await adminService.resetUserPassword(userId, { send_email: includeEmail, password: password });
        if (includeEmail && response.email_sent) {
          showSnackbar('הסיסמה אופסה ונשלחה למשתמש');
        } else {
          showSnackbar('הסיסמה אופסה בהצלחה');
        }
        setPasswordInfo(response);
        setPasswordDialogOpen(false);
      }

    } catch (error) {
      console.error(`Error ${action} password:`, error);
      showSnackbar(`שגיאה ב${action === 'get' ? 'קבלת' : action === 'send' ? 'שליחת' : 'איפוס'} הסיסמה`, 'error');
    } finally {
      setActionLoading(false);
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar('הסיסמה הועתקה ללוח');
    });
  };

  const handleExtendFreeTrial = useCallback(async (userId) => {
    try {
      setActionLoading(true);
      const response = await adminService.extendFreeTrial(userId);

      if (response.success) {
        // Update the user's subscription in the local state
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.id === userId && user.subscription) {
            return {
              ...user,
              subscription: {
                ...user.subscription,
                ...response.subscription
              }
            };
          }
          return user;
        }));

        showSnackbar(`תקופת הניסיון הוארכה ב-${response.days_extended} ימים`);
      }
    } catch (error) {
      console.error('Error extending free trial:', error);
      const errorMsg = error.response?.data?.error || 'שגיאה בהארכת תקופת הניסיון';
      showSnackbar(errorMsg, 'error');
    } finally {
      setActionLoading(false);
    }
  }, []);

  const columns = useMemo(() => [
    {
      field: 'id',
      headerName: 'מזהה',
      width: 80,
    },
    {
      field: 'full_name',
      headerName: 'שם',
      width: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'email',
      headerName: 'מייל',
      width: 200,
    },
    {
      field: 'phone',
      headerName: 'טלפון',
      width: 130,
    },
    {
      field: 'user_type_display',
      headerName: 'סוג משתמש',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.is_staff ? 'Admin' : params.value}
          size="small"
          color={params.row.user_type === 'trainer' ? 'primary' : 'secondary'}
        />
      ),
    },
    {
      field: 'status_display',
      headerName: 'סטטוס',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.row.status)}
        />
      ),
    },
    {
      field: 'city',
      headerName: 'עיר',
      width: 120,
    },
    {
      field: 'created_at',
      headerName: 'תאריך הרשמה',
      width: 130,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'total_bookings',
      headerName: 'הזמנות',
      width: 80,
      type: 'number',
    },
    {
      field: 'subscription',
      headerName: 'מנוי',
      width: 200,
      renderCell: (params) => {
        const subscription = params.value;
        const user = params.row;

        // Don't show subscription for studio users or admin users
        if (user.user_type === 'studio' || user.is_staff) {
          return '-';
        }

        if (!subscription) return 'אין מנוי';

        const isFreeTrialActive = subscription.plan === 'free_trial' && subscription.status === 'active';
        const label = isFreeTrialActive && subscription.end_date
          ? `נסיון עד ${formatDate(subscription.end_date)}`
          : subscription.plan;

        return (
          <Tooltip
            title={isFreeTrialActive && subscription.end_date
              ? `תקופת נסיון עד: ${formatDateTime(subscription.end_date)}`
              : subscription.plan
            }
            arrow
          >
            <Chip
              label={label}
              size="small"
              color={subscription.status === 'active' ? 'success' : 'warning'}
            />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      width: isMobile ? 280 : isTablet ? 320 : 400,
      minWidth: isMobile ? 250 : 300,
      flex: isMobile ? 1 : undefined,
      sortable: false,
      renderCell: (params) => {
        const buttonSize = isMobile ? 'small' : 'small';
        const showText = !isMobile;
        
        return (
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 0.3, 
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              height: '100%',
              py: 0.5,
              width: '100%',
              '& .MuiButton-root': {
                minWidth: isMobile ? '28px' : 'auto',
                fontSize: isMobile ? '0.65rem' : '0.7rem',
                fontWeight: 500,
                px: isMobile ? 0.3 : 0.8,
                py: 0.3,
              },
              '& .MuiButton-startIcon': {
                marginRight: isMobile ? 0 : '4px',
                marginLeft: 0,
                fontSize: '14px',
              }
            }}
          >
            <Tooltip title="צפה בפרטים">
              <Button
                size={buttonSize}
                variant="outlined"
                startIcon={<Visibility sx={{ fontSize: '14px' }} />}
                onClick={() => handleViewUser(params.row.id)}
                sx={{
                  minWidth: isMobile ? '28px' : '50px',
                  borderRadius: '4px',
                }}
              >
                {showText && 'צפה'}
              </Button>
            </Tooltip>
            
            <Tooltip title="ערוך משתמש">
              <Button
                size={buttonSize}
                variant="outlined"
                startIcon={<Edit sx={{ fontSize: '14px' }} />}
                onClick={() => handleEditUser(params.row)}
                color="info"
                sx={{
                  minWidth: isMobile ? '28px' : '50px',
                  borderRadius: '4px',
                }}
              >
                {showText && 'ערוך'}
              </Button>
            </Tooltip>

            <Tooltip title="ניהול סיסמה">
              <Button
                size={buttonSize}
                variant="outlined"
                startIcon={<VpnKey sx={{ fontSize: '14px' }} />}
                onClick={() => handlePasswordAction(params.row.id, 'get')}
                color="secondary"
                disabled={actionLoading}
                sx={{
                  minWidth: isMobile ? '28px' : '50px',
                  borderRadius: '4px',
                }}
              >
                {showText && 'סיסמה'}
              </Button>
            </Tooltip>

            {params.row.user_type === 'trainer' && params.row.subscription?.plan === 'free_trial' && (
              <Tooltip title="הארך תקופת ניסיון">
                <Button
                  size={buttonSize}
                  variant="contained"
                  startIcon={<EventAvailable sx={{ fontSize: '14px' }} />}
                  onClick={() => handleExtendFreeTrial(params.row.id)}
                  color="info"
                  disabled={actionLoading}
                  sx={{
                    minWidth: isMobile ? '28px' : '50px',
                    borderRadius: '4px',
                  }}
                >
                  {showText && 'הארך'}
                </Button>
              </Tooltip>
            )}

            {params.row.status === 'pending' && (
              <Tooltip title="אשר משתמש">
                <Button
                  size={buttonSize}
                  variant="contained"
                  startIcon={<CheckCircle sx={{ fontSize: '14px' }} />}
                  color="success"
                  onClick={() => handleUserAction(params.row.id, 'activate')}
                  disabled={actionLoading}
                  sx={{
                    minWidth: isMobile ? '28px' : '50px',
                    borderRadius: '4px',
                  }}
                >
                  {showText && 'אשר'}
                </Button>
              </Tooltip>
            )}
            
            {params.row.status === 'active' && (
              <>
                <Tooltip title="השעה משתמש">
                  <Button
                    size={buttonSize}
                    variant="contained"
                    startIcon={<Cancel sx={{ fontSize: '14px' }} />}
                    color="warning"
                    onClick={() => handleUserAction(params.row.id, 'deactivate')}
                    disabled={actionLoading}
                    sx={{
                      minWidth: isMobile ? '28px' : '50px',
                      borderRadius: '4px',
                    }}
                  >
                    {showText && 'השעה'}
                  </Button>
                </Tooltip>
                <Tooltip title="חסום משתמש">
                  <Button
                    size={buttonSize}
                    variant="contained"
                    color="error"
                    onClick={() => handleUserAction(params.row.id, 'suspend')}
                    disabled={actionLoading}
                    sx={{
                      minWidth: isMobile ? '28px' : '45px',
                      borderRadius: '4px',
                    }}
                  >
                    {showText && 'חסם'}
                  </Button>
                </Tooltip>
              </>
            )}
            
            {(params.row.status === 'inactive' || params.row.status === 'suspended') && (
              <Tooltip title="הפעל משתמש">
                <Button
                  size={buttonSize}
                  variant="contained"
                  startIcon={<CheckCircle sx={{ fontSize: '14px' }} />}
                  color="success"
                  onClick={() => handleUserAction(params.row.id, 'activate')}
                  disabled={actionLoading}
                  sx={{
                    minWidth: isMobile ? '28px' : '50px',
                    borderRadius: '4px',
                  }}
                >
                  {showText && 'הפעל'}
                </Button>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [users, isMobile, isTablet, actionLoading, handleExtendFreeTrial, handleViewUser, handleEditUser, handlePasswordAction, handleUserAction]);

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: isMobile ? 2 : 4, 
        mb: isMobile ? 2 : 4,
        px: isMobile ? 1 : 3,
      }}
    >
      <Typography variant="h4" gutterBottom>
        ניהול משתמשים
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ 
        p: isMobile ? 1.5 : 2, 
        mb: 3,
        borderRadius: 2,
        boxShadow: theme.shadows[2],
      }}>
        <Grid container spacing={isMobile ? 1.5 : 2} alignItems="center">
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              placeholder="חיפוש לפי שם, מייל או טלפון"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="status-filter-label">סטטוס</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter ?? ""}
                onChange={(e) => setStatusFilter(e.target.value)}
                input={<OutlinedInput label="סטטוס" />}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="">הכל</MenuItem>
                <MenuItem value="pending">ממתין לאישור</MenuItem>
                <MenuItem value="active">פעיל</MenuItem>
                <MenuItem value="inactive">לא פעיל</MenuItem>
                <MenuItem value="suspended">מושעה</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth variant="outlined" size={isMobile ? 'small' : 'medium'}>
              <InputLabel id="user-type-filter-label">סוג משתמש</InputLabel>
              <Select
                labelId="user-type-filter-label"
                id="user-type-filter"
                value={userTypeFilter ?? ""}
                onChange={(e) => setUserTypeFilter(e.target.value)}
                input={<OutlinedInput label="סוג משתמש" />}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              >
                <MenuItem value="">הכל</MenuItem>
                <MenuItem value="trainer">מאמן</MenuItem>
                <MenuItem value="studio">סטודיו</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2} lg={2}>
            <Button
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setUserTypeFilter('');
              }}
              sx={{
                borderRadius: 2,
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              נקה מסננים
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ 
        height: isMobile ? 500 : 600, 
        width: '100%', 
        overflow: 'hidden',
        borderRadius: 2,
        boxShadow: theme.shadows[3],
      }}>
        <DataGrid
          rows={users}
          columns={columns}
          pageSize={25}
          rowsPerPageOptions={[25, 50, 100]}
          loading={loading}
          disableSelectionOnClick
          rowHeight={70}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f5f5f5',
              borderBottom: '2px solid #e0e0e0',
              fontWeight: 'bold',
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
            },
            '& .MuiDataGrid-cell--withRenderer': {
              overflow: 'visible',
            },
            // Make table responsive
            '@media (max-width: 1200px)': {
              '& .MuiDataGrid-columnHeader[data-field="city"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="city"]': {
                display: 'none',
              },
            },
            '@media (max-width: 1000px)': {
              '& .MuiDataGrid-columnHeader[data-field="total_bookings"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="total_bookings"]': {
                display: 'none',
              },
              '& .MuiDataGrid-columnHeader[data-field="subscription"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="subscription"]': {
                display: 'none',
              },
            },
            '@media (max-width: 900px)': {
              '& .MuiDataGrid-columnHeader[data-field="created_at"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="created_at"]': {
                display: 'none',
              },
            },
            '@media (max-width: 800px)': {
              '& .MuiDataGrid-columnHeader[data-field="phone"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="phone"]': {
                display: 'none',
              },
            },
            '@media (max-width: 700px)': {
              '& .MuiDataGrid-columnHeader[data-field="user_type_display"]': {
                display: 'none',
              },
              '& .MuiDataGrid-cell[data-field="user_type_display"]': {
                display: 'none',
              },
            },
          }}
        />
      </Paper>

      {/* User Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: isMobile ? 0 : 2,
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid #e0e0e0',
          fontSize: isMobile ? '1.25rem' : '1.5rem'
        }}>
          פרטי משתמש
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedUser && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  פרטים אישיים
                </Typography>
                <Typography sx={{ mb: 1 }}><strong>שם:</strong> {selectedUser.full_name || 'לא צוין'}</Typography>
                <Typography sx={{ mb: 1 }}><strong>מייל:</strong> {selectedUser.email}</Typography>
                <Typography sx={{ mb: 1 }}><strong>טלפון:</strong> {selectedUser.phone}</Typography>
                <Typography sx={{ mb: 1 }}><strong>סוג:</strong> {selectedUser.user_type_display}</Typography>
                <Typography sx={{ mb: 1 }}><strong>סטטוס:</strong> {selectedUser.status_display}</Typography>
                <Typography sx={{ mb: 1 }}><strong>עיר:</strong> {selectedUser.city || 'לא צוין'}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  סטטיסטיקות
                </Typography>
                {selectedUser.booking_stats && (
                  <>
                    <Typography sx={{ mb: 1 }}><strong>סה״כ הזמנות:</strong> {selectedUser.booking_stats.total}</Typography>
                    <Typography sx={{ mb: 1 }}><strong>הזמנות שהושלמו:</strong> {selectedUser.booking_stats.completed}</Typography>
                    <Typography sx={{ mb: 1 }}><strong>הזמנות ממתינות:</strong> {selectedUser.booking_stats.pending}</Typography>
                  </>
                )}
                {selectedUser.payment_stats && (
                  <>
                    <Typography sx={{ mb: 1 }}><strong>סה״כ תשלומים:</strong> {selectedUser.payment_stats.total_payments}</Typography>
                    <Typography sx={{ mb: 1 }}><strong>סכום כולל:</strong> {formatCurrency(selectedUser.payment_stats.total_amount)}</Typography>
                  </>
                )}
              </Grid>

              {selectedUser.subscription && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    מנוי
                  </Typography>
                  <Typography sx={{ mb: 1 }}><strong>תוכנית:</strong> {selectedUser.subscription.plan}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>סטטוס:</strong> {selectedUser.subscription.status}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>תחילת מנוי:</strong> {formatDate(selectedUser.subscription.start_date)}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>סיום מנוי:</strong> {formatDate(selectedUser.subscription.end_date)}</Typography>
                </Grid>
              )}

              {selectedUser.trainer_profile && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    פרופיל מאמן
                  </Typography>
                  <Typography sx={{ mb: 1 }}><strong>ביוגרפיה:</strong> {selectedUser.trainer_profile.bio || 'לא צוין'}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>שנות ניסיון:</strong> {selectedUser.trainer_profile.experience_years}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>תעריף שעתי:</strong> {selectedUser.trainer_profile.hourly_rate ? formatCurrency(selectedUser.trainer_profile.hourly_rate) : 'לא צוין'}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>דירוג ממוצע:</strong> {selectedUser.trainer_profile.average_rating}/5</Typography>
                </Grid>
              )}

              {selectedUser.studio_profile && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    פרופיל סטודיו
                  </Typography>
                  <Typography sx={{ mb: 1 }}><strong>שם סטודיו:</strong> {selectedUser.studio_profile.studio_name}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>כתובת:</strong> {selectedUser.studio_profile.address || 'לא צוין'}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>תיאור:</strong> {selectedUser.studio_profile.description || 'לא צוין'}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>קיבולת:</strong> {selectedUser.studio_profile.capacity}</Typography>
                  <Typography sx={{ mb: 1 }}><strong>דירוג ממוצע:</strong> {selectedUser.studio_profile.average_rating}/5</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid #e0e0e0',
          p: 2,
          justifyContent: 'center'
        }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="contained"
            sx={{ 
              minWidth: '100px',
              borderRadius: 2,
            }}
          >
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          עריכת משתמש
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editingUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="שם מלא"
                    value={editingUser.full_name || ''}
                    onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="מייל"
                    value={editingUser.email || ''}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    margin="normal"
                    type="email"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="טלפון"
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>סטטוס</InputLabel>
                    <Select
                      value={editingUser.status || ''}
                      onChange={(e) => setEditingUser({...editingUser, status: e.target.value})}
                      input={<OutlinedInput label="סטטוס" />}
                    >
                      <MenuItem value="pending">ממתין לאישור</MenuItem>
                      <MenuItem value="active">פעיל</MenuItem>
                      <MenuItem value="inactive">לא פעיל</MenuItem>
                      <MenuItem value="suspended">מושעה</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="עיר"
                    value={editingUser.city || ''}
                    onChange={(e) => setEditingUser({...editingUser, city: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="רחוב"
                    value={editingUser.street || ''}
                    onChange={(e) => setEditingUser({...editingUser, street: e.target.value})}
                    margin="normal"
                  />
                </Grid>
                
                {editingUser.trainer_profile && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>
                        <Typography variant="h6">פרופיל מאמן</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="ביוגרפיה"
                        value={editingUser.trainer_profile.bio || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          trainer_profile: {...editingUser.trainer_profile, bio: e.target.value}
                        })}
                        margin="normal"
                        multiline
                        rows={3}
                      />
                    </Grid>
                    {/* <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="שנות ניסיון"
                        value={editingUser.trainer_profile.experience_years || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          trainer_profile: {...editingUser.trainer_profile, experience_years: e.target.value}
                        })}
                        margin="normal"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="תעריף שעתי"
                        value={editingUser.trainer_profile.hourly_rate || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          trainer_profile: {...editingUser.trainer_profile, hourly_rate: e.target.value}
                        })}
                        margin="normal"
                        type="number"
                      />
                    </Grid> */}
                  </>
                )}
                
                {editingUser.studio_profile && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>
                        <Typography variant="h6">פרופיל סטודיו</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="שם סטודיו"
                        value={editingUser.studio_profile.studio_name || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          studio_profile: {...editingUser.studio_profile, studio_name: e.target.value}
                        })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="כתובת"
                        value={editingUser.studio_profile.address || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          studio_profile: {...editingUser.studio_profile, address: e.target.value}
                        })}
                        margin="normal"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="תיאור"
                        value={editingUser.studio_profile.description || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          studio_profile: {...editingUser.studio_profile, description: e.target.value}
                        })}
                        margin="normal"
                        multiline
                        rows={3}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="קיבולת"
                        value={editingUser.studio_profile.capacity || ''}
                        onChange={(e) => setEditingUser({
                          ...editingUser,
                          studio_profile: {...editingUser.studio_profile, capacity: e.target.value}
                        })}
                        margin="normal"
                        type="number"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>
            בטל
          </Button>
          <Button 
            onClick={handleSaveUser}
            variant="contained"
            startIcon={actionLoading ? <CircularProgress size={16} /> : <Save />}
            disabled={actionLoading}
          >
            שמור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Management Dialog */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          ניהול סיסמה
          <IconButton
            onClick={() => setPasswordDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {passwordInfo && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                פרטי משתמש:
              </Typography>
              <Typography sx={{ mb: 1 }}><strong>מייל:</strong> {passwordInfo.email}</Typography>
              
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mt: 2, 
                p: 2, 
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #dee2e6'
              }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  סיסמה חדשה:
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    fontSize: '1.1rem',
                    color: 'primary.main',
                    fontWeight: 'bold'
                  }}
                >
                  {passwordInfo.temp_password || passwordInfo.new_password}
                </Typography>
                <IconButton 
                  onClick={() => copyToClipboard(passwordInfo.temp_password || passwordInfo.new_password)}
                  size="small"
                  title="העתק ללוח"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ mt: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<Send />}
                  onClick={() => {
                    handlePasswordAction(passwordInfo.user_id, 'reset', true, passwordInfo.temp_password || passwordInfo.new_password);
                    setPasswordDialogOpen(false);
                  }}
                  disabled={actionLoading}
                >
                  אפס ושלח
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>
            סגור
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;